/**
 * Buckets Brief — the JAS Bollinger distribution, one panel per list.
 *
 * Drawn as SVG, not SciChart: twelve bars a panel and nothing to pan or zoom,
 * so WebGL buys nothing and SVG takes the theme's CSS variables directly.
 * Today is coloured bars with their value on top and a line along their tops;
 * yesterday is grey bars behind. Live during the session (refreshed every
 * 60 s); a date box jumps to any session. On a phone one list at a time, with
 * swipe and ‹ › — this panel has no pan for a swipe to fight, unlike the chart
 * (`docs/features/mobile.md`).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { BucketsBriefConfig } from "@qufolio/model";
import {
  fetchBucketSeries, fetchBuckets, type BucketSeries, type BucketsPayload, type ListBuckets, type PipData,
} from "../api/buckets";
import {
  BUCKETS, REPLAY_DEFAULT, REPLAY_MAX, REPLAY_SPEEDS, REPLAY_STEP_MS,
  CSS_PALETTE, PIP_FRACTION, axisTop, bucketColorIn, pipPlacement, replayFrame, replayLength, resolvePalette,
  marketToday, nextWeekday, stepList, toWeekday, type Palette, type PipPlacement, type PipSize,
} from "../lib/buckets-brief";
import { useIsNarrow } from "../lib/use-narrow";
import { BRAND } from "../lib/brand-current";
import { DataCards } from "./DataCards";
import { useTheme } from "./theme";

const REFRESH_MS = 60_000;
/** How long the date box must be still before its date is loaded. */
export const DATE_SETTLE_MS = 500;

interface Replay {
  series: BucketSeries;
  /** The frame shown; 1 is the first session (0 is only its grey "yesterday"). */
  index: number;
  playing: boolean;
}

export function BucketsBriefView({
  config,
  cards = false,
}: {
  config: BucketsBriefConfig;
  /**
   * The touch shell: one list a card, swiped, with the date row shared above
   * them (`docs/features/mobile.md`, "The briefs are cards too").
   */
  cards?: boolean;
}) {
  const lists = config.lists;
  const theme = useTheme();
  const [date, setDate] = useState("");
  const [data, setData] = useState<BucketsPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [days, setDays] = useState(REPLAY_DEFAULT);
  const [speed, setSpeed] = useState<number>(1);
  const [replay, setReplay] = useState<Replay | null>(null);
  const [loading, setLoading] = useState(false);
  const [pipSize, setPipSize] = useState<PipSize>(readPipSize);
  const [gifNote, setGifNote] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const narrow = useIsNarrow();

  // What the date box holds, apart from `date`, the session actually loaded.
  // The browser's date input reports every complete date on the way — each
  // arrow-key step of the month, each digit typed over — so loading on every
  // change fetched each month in between (owner, 2026-10-01). It commits once
  // the box has been still for `DATE_SETTLE_MS`, or at once on Enter or blur.
  const [draft, setDraft] = useState("");
  // A weekend becomes its Friday on the way in, and the box shows the Friday.
  const commit = useCallback((value: string) => {
    const session = toWeekday(value);
    setDraft(session);
    setDate(session);
  }, []);
  useEffect(() => {
    if (draft === date) return;
    const timer = setTimeout(() => commit(draft), DATE_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [draft, date, commit]);

  // A › in flight: the session it stepped from, and the weekday it asked for.
  // When that weekday was a holiday the server answers with the session before
  // it — the one stepped from — and the step moves on a day instead of drawing.
  const step = useRef<{ from: string; target: string } | null>(null);

  // Only the newest request may draw. Replies can land out of order — a date
  // before 2020 reads the stored counts, a recent one computes — and the
  // slower, older one overwrote the date that was asked for last.
  const latest = useRef(0);
  // The session a load the user asked for is fetching, for the loading signal
  // (owner, 2026-10-01). The quiet 60 s live refresh never sets it, or the
  // panel would flicker every minute.
  const [loadingDate, setLoadingDate] = useState<string | null>(null);
  const load = useCallback((quiet = false) => {
    const ticket = ++latest.current;
    if (!quiet) setLoadingDate(date || "today");
    fetchBuckets(lists, date || undefined)
      .then((payload) => {
        if (ticket !== latest.current) return;
        const pending = step.current;
        step.current = null;
        const got = Object.values(payload.lists).find(Boolean)?.date;
        if (pending && date === pending.target && got && got <= pending.from) {
          const next = nextWeekday(pending.target);
          if (next >= marketToday()) {
            commit("");
          } else {
            step.current = { from: pending.from, target: next };
            commit(next);
          }
          return; // still loading: the hop's own load takes over the signal
        }
        setData(payload);
        setError(null);
        setLoadingDate(null);
      })
      .catch((e: unknown) => {
        if (ticket !== latest.current) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoadingDate(null);
      });
  }, [lists, date, commit]);

  useEffect(() => {
    load();
    // Live only when no date is chosen: a past session does not move.
    if (date) return;
    const timer = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(timer);
  }, [load, date]);

  const frames = replay ? replayLength(replay.series.lists) : 0;

  // The playhead. One timer per speed; it stops itself on the last frame and
  // leaves that frame up, which is the session the replay was ending at.
  useEffect(() => {
    if (!replay?.playing) return;
    const timer = setInterval(() => {
      setReplay((r) => {
        if (!r) return r;
        const last = replayLength(r.series.lists) - 1;
        return r.index >= last ? { ...r, playing: false } : { ...r, index: r.index + 1 };
      });
    }, REPLAY_STEP_MS / speed);
    return () => clearInterval(timer);
  }, [replay?.playing, speed]);

  const startReplay = () => {
    setHeldPips(null);
    setLoading(true);
    fetchBucketSeries(lists, days, date || undefined)
      .then((series) => {
        setReplay({ series, index: 1, playing: true });
        setError(null);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  };

  /**
   * A GIF of the same N sessions a replay plays, at the replay's speed (owner,
   * 2026-10-01). Loaded on first use, so the encoder and the static renderer
   * never weigh on a page that does not make one.
   */
  const makeGif = async () => {
    try {
      setGifNote("Preparing…");
      const series = replay?.series ?? (await fetchBucketSeries(lists, days, date || undefined));
      const count = replayLength(series.lists);
      if (count < 2) throw new Error("Not enough sessions for a GIF.");
      const style = getComputedStyle(rootRef.current ?? document.documentElement);
      const palette = resolvePalette((name) => style.getPropertyValue(name));
      const { buildGif, gifName } = await import("./BucketsBriefGif");
      const blob = await buildGif(series, lists, count, REPLAY_STEP_MS / speed, palette, pipSize,
        BRAND.identity.name, (done, total) => setGifNote(`Building GIF ${done}/${total}…`));
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = gifName(series, lists, count);
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      setGifNote(null);
    } catch (e: unknown) {
      setGifNote(null);
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  /**
   * The PIPs held still while days are clicked in them (owner, 2026-10-07,
   * option B). A load re-anchors each PIP to end on its session, so a clicked
   * day would become the last candle and every later one would vanish — you
   * could only ever click backwards. Held, the 60 candles stay and the band
   * moves. Every other way of moving lets go: the date box, ‹ ›, Today, Replay.
   */
  const [heldPips, setHeldPips] = useState<BucketsPayload["pips"] | null>(null);
  const pickDay = (day: string) => {
    if (!replay) setHeldPips((held) => held ?? data?.pips ?? null);
    else setHeldPips(replay.series.pips ?? null);
    step.current = null;
    setReplay(null);
    commit(day);
  };

  const shownFor = (code: string): ListBuckets | null =>
    replay ? replayFrame(replay.series.lists[code], replay.index) : (data?.lists[code] ?? null);

  const first = lists.map(shownFor).find(Boolean) ?? null;

  // ‹ › step one session from the one on screen — a replay frame included,
  // which ends the replay. Back is the "vs" date, so a holiday is already
  // skipped; forward is the next weekday, and a holiday is skipped in `load`.
  const stepBack = () => {
    if (!first) return;
    setHeldPips(null);
    step.current = null;
    setReplay(null);
    setDraft(first.prevDate);
  };
  const forwardTarget = first ? nextWeekday(first.date) : null;
  const atLatest = !first || (!date && !replay) || (forwardTarget ?? "") > marketToday();
  const stepForward = () => {
    if (!first || !forwardTarget || atLatest) return;
    setHeldPips(null);
    setReplay(null);
    if (forwardTarget === marketToday()) {
      step.current = null;
      setDraft("");
    } else {
      step.current = { from: first.date, target: forwardTarget };
      setDraft(forwardTarget);
    }
  };
  const touch = useRef<number | null>(null);
  const panel = (code: string) => (
    <Panel
      code={code}
      data={shownFor(code)}
      pip={(replay ? replay.series.pips : (heldPips ?? data?.pips))?.[code]}
      size={pipSize}
      onPick={pickDay}
    />
  );
  const shown = narrow ? [lists[page % lists.length]!] : lists;

  // One signal for every way of moving: a replay being fetched, a date still
  // settling in the box or behind ‹ ›, and the load it then makes.
  const busy = loading
    ? `loading ${days} sessions…`
    : draft !== date
      ? `loading ${toWeekday(draft) || "today"}…`
      : loadingDate
        ? `loading ${loadingDate}…`
        : null;

  return (
    <div style={ROOT} ref={rootRef}>
      {/* Left | centre | right (owner's mock, 2026-09-30): the date being
          shown and every control that moves it sit together in the middle. */}
      <div style={narrow ? BAR_NARROW : BAR}>
        <div style={SIDE_LEFT}>
          <strong style={{ fontSize: 13 }}>Buckets Brief</strong>
          {data?.live && !replay && !busy && <span style={LIVE}>live</span>}
          {/* Left, not beside the date: there it would shove the centred
              group sideways as it came and went, moving ‹ › under the pointer. */}
          {busy && <span style={MUTED}>⟳ {busy}</span>}
        </div>
        <div style={CENTER}>
        {first && (
          // In the accent, so it can be followed as a replay moves. A click
          // steps one session while a replay is paused.
          <button
            type="button"
            aria-label={`${first.date} versus ${first.prevDate}`}
            onClick={() =>
              setReplay((r) => (r && !r.playing && r.index < frames - 1 ? { ...r, index: r.index + 1 } : r))
            }
            style={DATE_LABEL}
          >
            {first.date}
            <span style={{ fontSize: "0.65em", opacity: 0.85 }}> vs {first.prevDate}</span>
          </button>
        )}
        <button type="button" aria-label="Previous session" title="Previous session"
          onClick={stepBack} disabled={!first} style={TODAY}>
          ‹
        </button>
        <input
          type="date"
          aria-label="Jump to date"
          // The session on screen when none is chosen — today while live, else
          // the last trading day — as the chart's Go-to-date box shows it. An
          // empty `mm/dd/yyyy` said nothing about what was drawn (owner, 2026-10-18).
          value={draft || first?.date || ""}
          max={marketToday()}
          onChange={(e) => {
            step.current = null;
            setHeldPips(null);
            setReplay(null);
            setDraft(e.target.value);
          }}
          onBlur={() => commit(draft)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit(draft);
          }}
          // The calendar glyph is the browser's: without the theme's mode it is
          // drawn dark on the dark bar, as `GoToDateBox` explains.
          style={{ ...DATE, colorScheme: theme.mode }}
        />
        <button type="button" aria-label="Next session" title="Next session"
          onClick={stepForward} disabled={atLatest} style={TODAY}>
          ›
        </button>
        <button
          type="button"
          onClick={() => {
            setHeldPips(null);
            setReplay(null);
            setDraft("");
            setDate("");
          }}
          disabled={!draft && !date && !replay}
          style={TODAY}
        >
          Today
        </button>
        <span style={MUTED}>Replay</span>
        <input
          type="number"
          aria-label="Replay sessions"
          min={1}
          max={REPLAY_MAX}
          value={days}
          onChange={(e) => setDays(Math.max(1, Math.min(REPLAY_MAX, Number(e.target.value) || REPLAY_DEFAULT)))}
          style={{ ...DATE, width: 52 }}
        />
        <span style={MUTED}>days</span>
        {replay?.playing ? (
          <button
            type="button"
            aria-label="Pause replay"
            style={TODAY}
            onClick={() => setReplay((r) => (r ? { ...r, playing: false } : r))}
          >
            ⏸
          </button>
        ) : (
          <button
            type="button"
            aria-label="Play replay"
            style={TODAY}
            disabled={loading}
            onClick={() => {
              // Resume where it paused; from the start once it has finished.
              if (replay && replay.index < frames - 1) setReplay({ ...replay, playing: true });
              else startReplay();
            }}
          >
            {loading ? "…" : "▶"}
          </button>
        )}
        {REPLAY_SPEEDS.map((x) => (
          <button
            key={x}
            type="button"
            aria-pressed={speed === x}
            onClick={() => setSpeed(x)}
            style={{ ...TODAY, ...(speed === x ? SPEED_ON : null) }}
          >
            {x}x
          </button>
        ))}
        <button type="button" onClick={makeGif} disabled={gifNote !== null} style={TODAY}
          title="Download the replay's sessions as a GIF">
          {gifNote ?? "⤓ GIF"}
        </button>
        </div>
        <div style={SIDE_RIGHT}>
        <span style={MUTED}>PIP</span>
        {(["small", "mid"] as const).map((z) => (
          <button
            key={z}
            type="button"
            aria-pressed={pipSize === z}
            onClick={() => {
              setPipSize(z);
              writePipSize(z);
            }}
            style={{ ...TODAY, ...(pipSize === z ? SPEED_ON : null) }}
          >
            {z === "small" ? "S" : "M"}
          </button>
        ))}
        </div>
      </div>
      {error && <div style={ERROR}>{error}</div>}
      <div
        style={{ ...(narrow || cards ? ONE : GRID), position: "relative" }}
        onTouchStart={(e) => {
          touch.current = e.touches[0]?.clientX ?? null;
        }}
        onTouchEnd={(e) => {
          const start = touch.current;
          const end = e.changedTouches[0]?.clientX;
          touch.current = null;
          if (!narrow || cards || start == null || end == null || Math.abs(end - start) < 40) return;
          setPage((p) => stepList(p, end < start ? 1 : -1, lists.length));
        }}
      >
        {busy && (
          // Over the panels' top edge rather than a row of its own, so the
          // grid does not move when it appears.
          <div role="progressbar" aria-label={busy} style={BAR_TRACK}>
            <style>{`@keyframes qf-bb-load { from { left: -30%; } to { left: 100%; } }`}</style>
            <div style={BAR_RUN} />
          </div>
        )}
        {cards ? (
          <div style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
            <DataCards
              labels={lists.map((code) => ({ id: code, label: code, chip: code }))}
              active={lists[page % lists.length]!}
              onActive={(code) => setPage(lists.indexOf(code))}
              strip={false}
              render={(code) => <div style={{ height: "100%", display: "flex" }}>{panel(code)}</div>}
            />
          </div>
        ) : (
          shown.map((code) => (
            <div key={code} style={PANEL_SLOT}>
              {panel(code)}
            </div>
          ))
        )}
      </div>
      {narrow && !cards && (
        <div style={PAGER}>
          <button type="button" aria-label="Previous list" style={ARROW}
            onClick={() => setPage((p) => stepList(p, -1, lists.length))}>‹</button>
          {lists.map((code, i) => (
            <button key={code} type="button" aria-label={code} onClick={() => setPage(i)}
              style={{ ...DOT, opacity: i === page % lists.length ? 1 : 0.35 }} />
          ))}
          <button type="button" aria-label="Next list" style={ARROW}
            onClick={() => setPage((p) => stepList(p, 1, lists.length))}>›</button>
        </div>
      )}
    </div>
  );
}

const W = 600;
const H = 300;
const DEFAULT_H = H;
// Tight: the frame only has to hold the axis numbers (owner, 2026-10-01 —
// the first margins left too much of the frame showing).
const PAD = { left: 24, right: 4, top: 8, bottom: 20 };

/**
 * The drawing's height for a box of this shape, at a fixed width of `W`. A
 * fixed 2:1 viewBox letterboxed every cell that was not exactly 2:1 — bands of
 * frame above and below each chart (owner, 2026-10-01) — so the drawing takes
 * the cell's own proportions instead, clamped so a very flat or tall cell
 * still draws a usable chart.
 */
export function panelHeight(width: number, height: number): number {
  if (!(width > 0) || !(height > 0)) return H;
  return Math.min(Math.max((W * height) / width, 160), 900);
}

function Panel({
  code, data, pip, size, onPick,
}: {
  code: string; data: ListBuckets | null; pip: PipData | null | undefined; size: PipSize;
  onPick: (date: string) => void;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [h, setH] = useState(H);
  useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setH(panelHeight(width, height));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div style={PANEL} ref={boxRef}>
      <svg viewBox={`0 0 ${W} ${h}`} preserveAspectRatio="xMidYMid meet"
        style={{ width: "100%", height: "100%", display: "block" }}
        role="img" aria-label={`${code} Bollinger distribution`}>
        <PanelArt code={code} data={data} pip={pip} size={size} palette={CSS_PALETTE} h={h} onPick={onPick} />
      </svg>
    </div>
  );
}

/**
 * One panel's drawing, in a `W`×`h` box — shared by the screen and the GIF.
 *
 * Every colour comes from `palette`: the theme's CSS variables on screen, the
 * same tokens resolved to real colours for a GIF frame, where `var()` means
 * nothing. One drawing for both is what keeps a GIF looking like the panel.
 */
export function PanelArt({
  code, data, pip, size, palette, h: H = DEFAULT_H, onPick,
}: {
  code: string; data: ListBuckets | null; pip: PipData | null | undefined; size: PipSize; palette: Palette;
  /** The drawing's height; the width is always `W`. */
  h?: number;
  onPick?: (date: string) => void;
}) {
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const slot = plotW / BUCKETS.length;
  const top = data ? axisTop(data.today, data.yesterday) : 20;
  const y = (v: number) => PAD.top + plotH - (v / top) * plotH;
  const cx = (b: number) => PAD.left + slot * b + slot / 2;
  const place = data ? pipPlacement(data.today, data.yesterday, top, size) : null;
  const axis = { fontSize: 11, fill: palette.muted };
  return (
    <>
      {/* Both surfaces, as a chart has them (owner, 2026-10-01): the frame on
          `surface`, the plot area inside the axes on `surface-alt`. */}
      <rect x={0} y={0} width={W} height={H} fill={palette.surface} />
      <rect x={PAD.left} y={PAD.top} width={plotW} height={plotH} fill={palette.surfaceAlt} />
      <text x={W / 2} y={PAD.top + plotH * 0.22} textAnchor="middle"
        style={{ fontSize: 40, fill: palette.muted, opacity: 0.35 }}>{code}</text>
      {[0, 0.25, 0.5, 0.75, 1].map((f) => (
        <g key={f}>
          <line x1={PAD.left} x2={W - PAD.right} y1={y(top * f)} y2={y(top * f)} stroke={palette.border} strokeDasharray="3 4" />
          <text x={PAD.left - 4} y={y(top * f) + 4} textAnchor="end" style={axis}>{Math.round(top * f)}</text>
        </g>
      ))}
      {data &&
        BUCKETS.map((b) => {
          const t = data.today[b] ?? 0;
          const yd = data.yesterday[b] ?? 0;
          return (
            <g key={b}>
              <title>{`Bucket ${b}: today ${t}% · ${data.prevDate} ${yd}%`}</title>
              <rect x={cx(b) - slot * 0.4} width={slot * 0.8} y={y(yd)} height={y(0) - y(yd)} fill={palette.yesterday} />
              <rect x={cx(b) - slot * 0.22} width={slot * 0.44} y={y(t)} height={y(0) - y(t)} fill={bucketColorIn(palette, b)} />
              <text x={cx(b)} y={y(t) - 4} textAnchor="middle" style={{ fontSize: 11, fill: palette.text }}>{t}</text>
            </g>
          );
        })}
      {data && (
        <polyline
          fill="none"
          stroke={palette.positive}
          strokeWidth={1.5}
          points={BUCKETS.map((b) => `${cx(b)},${y(data.today[b] ?? 0)}`).join(" ")}
        />
      )}
      {BUCKETS.map((b) => (
        <text key={b} x={cx(b)} y={H - 8} textAnchor="middle" style={axis}>{b}</text>
      ))}
      {!data && <text x={W / 2} y={H / 2} textAnchor="middle" style={axis}>No data</text>}
      {data && pip && place && (
        <Pip pip={pip} date={data.date} size={size} place={place} palette={palette} onPick={onPick}
          plot={{ x: PAD.left, y: PAD.top, w: plotW, h: plotH }} />
      )}
    </>
  );
}

/**
 * The list's own index as daily candles, inside the panel — the session in
 * view on a highlighted band that moves with a replay.
 */
function Pip({
  pip, date, size, place, plot, palette, onPick,
}: {
  pip: PipData;
  date: string;
  size: PipSize;
  place: PipPlacement;
  plot: { x: number; y: number; w: number; h: number };
  palette: Palette;
  /** Load a session by clicking its candle — on screen only; a GIF has none. */
  onPick?: (date: string) => void;
}) {
  const { w: fw, h: fh } = PIP_FRACTION[size];
  const w = plot.w * fw;
  const h = plot.h * fh;
  const x0 = place.side === "left" ? plot.x + 4 : place.side === "right" ? plot.x + plot.w - w - 4 : plot.x + (plot.w - w) / 2;
  const y0 = plot.y + 2;
  const bars = pip.bars;
  if (!bars.length) return null;
  const lo = Math.min(...bars.map((b) => b[3]));
  const hi = Math.max(...bars.map((b) => b[2]));
  const inner = { x: x0 + 4, y: y0 + 14, w: w - 8, h: h - 18 };
  const step = inner.w / bars.length;
  const py = (v: number) => inner.y + inner.h - ((v - lo) / (hi - lo || 1)) * inner.h;
  const at = bars.findIndex((b) => b[0] === date);
  return (
    <g opacity={place.transparent ? 0.55 : 1}>
      <rect x={x0} y={y0} width={w} height={h} rx={4} fill={palette.surface} stroke={palette.border}
        opacity={place.transparent ? 0.6 : 0.92} />
      <text x={x0 + 6} y={y0 + 11} style={{ fontSize: 10, fill: palette.muted }}>{pip.symbol} · 1D</text>
      {at >= 0 && (
        <rect x={inner.x + step * at} y={inner.y} width={Math.max(step, 2)} height={inner.h} fill={palette.accent} opacity={0.28} />
      )}
      {bars.map(([d, o, hgh, l, c], i) => {
        const color = c >= o ? palette.positive : palette.negative;
        const mid = inner.x + step * i + step / 2;
        return (
          <g key={d}>
            <line x1={mid} x2={mid} y1={py(hgh)} y2={py(l)} stroke={color} strokeWidth={1} />
            <rect x={mid - step * 0.32} width={step * 0.64} y={py(Math.max(o, c))}
              height={Math.max(1, Math.abs(py(o) - py(c)))} fill={color} />
          </g>
        );
      })}
      {/* Click a day to load it (owner, 2026-10-07). The whole column is the
          target, not the thin body — a candle is a few pixels wide, less on a
          phone. Drawn last, so it is on top of the candles it covers. */}
      {onPick && (
        <>
          <style>{".qf-pip-day{fill:transparent;cursor:pointer}.qf-pip-day:hover{fill:var(--qf-accent);fill-opacity:.18}"}</style>
          {bars.map(([d], i) => (
            <rect key={d} className="qf-pip-day" data-date={d} x={inner.x + step * i} y={inner.y}
              width={step} height={inner.h} onClick={() => onPick(d)}>
              <title>{d}</title>
            </rect>
          ))}
        </>
      )}
    </g>
  );
}

const ROOT: React.CSSProperties = {
  width: "100%", height: "100%", display: "flex", flexDirection: "column",
  background: "var(--qf-background)", color: "var(--qf-text)", minHeight: 0,
};
const BAR: React.CSSProperties = {
  display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: 8,
  padding: "4px 8px", borderBottom: "1px solid var(--qf-border)",
};
/** A phone: the title row, then the centred group wrapped beneath it. */
const BAR_NARROW: React.CSSProperties = {
  display: "flex", flexWrap: "wrap", justifyContent: "center", alignItems: "center", gap: 6,
  padding: "4px 8px", borderBottom: "1px solid var(--qf-border)",
};
const SIDE_LEFT: React.CSSProperties = { display: "flex", alignItems: "center", gap: 8, justifySelf: "start" };
const SIDE_RIGHT: React.CSSProperties = { display: "flex", alignItems: "center", gap: 6, justifySelf: "end" };
const CENTER: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", justifyContent: "center",
}
const MUTED: React.CSSProperties = { color: "var(--qf-text-muted)", fontSize: 12 };
const BAR_TRACK: React.CSSProperties = {
  position: "absolute", top: 0, left: 0, right: 0, height: 2, overflow: "hidden", zIndex: 2,
};
const BAR_RUN: React.CSSProperties = {
  position: "absolute", top: 0, width: "30%", height: "100%", background: "var(--qf-accent)",
  animation: "qf-bb-load 1s linear infinite",
};
/**
 * Holds one panel in its grid cell. Panels are not dimmed while loading — the
 * owner tried it and did not like it (2026-10-01); the bar and the label say it.
 */
const PANEL_SLOT: React.CSSProperties = { flex: 1, display: "flex", minWidth: 0, minHeight: 0 };
const LIVE: React.CSSProperties = {
  fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em",
  color: "var(--qf-positive)", border: "1px solid var(--qf-positive)", borderRadius: 4, padding: "0 4px",
};
const DATE: React.CSSProperties = {
  background: "var(--qf-surface)", color: "var(--qf-text)", border: "1px solid var(--qf-border)",
  borderRadius: 4, padding: "2px 4px", fontSize: 12,
};
const TODAY: React.CSSProperties = { ...DATE, cursor: "pointer" };
const ERROR: React.CSSProperties = { color: "var(--qf-negative)", fontSize: 12, padding: "4px 8px" };
const GRID: React.CSSProperties = {
  flex: 1, minHeight: 0, display: "grid", gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr", gap: 2,
};
const ONE: React.CSSProperties = { flex: 1, minHeight: 0, display: "flex" };
const PANEL: React.CSSProperties = {
  // Both surfaces, as a chart has them (owner, 2026-10-01): the frame with the
  // axes on `surface`, the plot area inside them on `surface-alt` — the white
  // of one alone read too bright on a light theme. The PIP sits on `surface`.
  flex: 1, minWidth: 0, minHeight: 0, background: "var(--qf-surface)", border: "1px solid var(--qf-border)",
};
const PAGER: React.CSSProperties = {
  display: "flex", justifyContent: "center", alignItems: "center", gap: 10, padding: 6,
};
const ARROW: React.CSSProperties = {
  background: "none", border: "none", color: "var(--qf-text)", fontSize: 22, cursor: "pointer", padding: "0 8px",
};
const DOT: React.CSSProperties = {
  width: 8, height: 8, borderRadius: 4, border: "none", background: "var(--qf-text)", padding: 0, cursor: "pointer",
};
const SPEED_ON: React.CSSProperties = { borderColor: "var(--qf-accent)", color: "var(--qf-accent)" };
const DATE_LABEL: React.CSSProperties = {
  background: "none", border: "none", padding: "0 6px", cursor: "pointer",
  color: "var(--qf-accent)", fontSize: 18, fontWeight: 600, whiteSpace: "nowrap",
};

const PIP_KEY = "qf.bucketsBrief.pipSize";

/** The PIP size this browser last chose — a convenience, so a failure reads as "small". */
function readPipSize(): PipSize {
  try {
    return localStorage.getItem(PIP_KEY) === "mid" ? "mid" : "small";
  } catch {
    return "small";
  }
}

function writePipSize(size: PipSize): void {
  try {
    localStorage.setItem(PIP_KEY, size);
  } catch {
    // A private window: the choice simply is not remembered.
  }
}
